import React, { useState, useEffect, useCallback, useRef } from 'react';
import './TilePile.css';
import './GameModal.css';
import wordList from 'content/en/enable1_words.json';
import ActionButton from '../buttons/ActionButton';
import {
  faSortAlphaDown,
  faRandom,
  faCheckToSlot,
} from '@fortawesome/free-solid-svg-icons';
import IconButton from '../buttons/IconButton';
import CollapsibleWordList from '../common/CollapsibleWordList';
import GameOver from './GameOver';
import { GameScore } from 'services/gameService';

const beepSound = require('assets/sounds/beep-1.mp3');
const wrongSound = require('assets/sounds/wrong-1.mp3');
const selectSound = require('assets/sounds/select-1.mp3');
const removeSound = require('assets/sounds/remove-1.mp3');

// Define the type for our word list
type WordList = { [key: string]: number };

interface TilePileProps {
  isOpen?: boolean;
  onClose?: () => void;
  onHighScore?: (score: number) => void;
  gameScore?: GameScore;
  gameId: number;
}

// Letter frequency data based on English language statistics
const LETTER_FREQUENCIES: { [key: string]: number } = {
  E: 12.02,
  T: 9.1,
  A: 8.12,
  O: 7.68,
  I: 7.31,
  N: 6.95,
  S: 6.28,
  R: 6.02,
  H: 5.92,
  D: 4.32,
  L: 3.98,
  U: 2.88,
  C: 2.71,
  M: 2.61,
  F: 2.3,
  Y: 2.11,
  W: 2.09,
  G: 2.03,
  P: 1.82,
  B: 1.49,
  V: 1.11,
  K: 0.69,
  X: 0.17,
  Q: 0.11,
  J: 0.1,
  Z: 0.07,
};

// Normalize frequencies to sum to 1
const TOTAL_FREQUENCY = Object.values(LETTER_FREQUENCIES).reduce(
  (sum, freq) => sum + freq,
  0,
);
const NORMALIZED_FREQUENCIES: { [key: string]: number } = {};
Object.entries(LETTER_FREQUENCIES).forEach(([letter, freq]) => {
  NORMALIZED_FREQUENCIES[letter] = freq / TOTAL_FREQUENCY;
});

const VOWELS = ['A', 'E', 'I', 'O', 'U'];
const CONSONANTS = 'BCDFGHJKLMNPQRSTVWXYZ'.split('');
const GAME_TIME = 60;

interface FloatingScore {
  id: number;
  points: number;
  x: number;
  y: number;
}

export const TilePileGameModal: React.FC<TilePileProps> = ({
  isOpen = true,
  onClose,
  onHighScore,
  gameScore,
  gameId,
}) => {
  const [letters, setLetters] = useState<string[]>([]);
  const [currentWord, setCurrentWord] = useState<string[]>([]);
  const [usedWords, setUsedWords] = useState<string[]>([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [timeLeft, setTimeLeft] = useState(GAME_TIME);
  const [usedLetters, setUsedLetters] = useState<Set<string>>(new Set());
  const [letterUsage, setLetterUsage] = useState<{ [key: string]: number }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [attemptsThisRound, setAttemptsThisRound] = useState(0);
  const [floatingScores, setFloatingScores] = useState<FloatingScore[]>([]);
  const nextScoreId = useRef(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const wrongAudioRef = useRef<HTMLAudioElement | null>(null);
  const selectAudioRef = useRef<HTMLAudioElement | null>(null);
  const removeAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isInvalidWord, setIsInvalidWord] = useState(false);

  // Refs to access current state in callbacks
  const lettersRef = useRef(letters);
  const letterUsageRef = useRef(letterUsage);
  const currentWordRef = useRef(currentWord);
  const gameOverRef = useRef(gameOver);
  const attemptsRef = useRef(attemptsThisRound);

  // Update refs when state changes
  useEffect(() => {
    lettersRef.current = letters;
    letterUsageRef.current = letterUsage;
    currentWordRef.current = currentWord;
    gameOverRef.current = gameOver;
    attemptsRef.current = attemptsThisRound;
  }, [letters, letterUsage, currentWord, gameOver, attemptsThisRound]);

  // Initialize audio elements
  useEffect(() => {
    audioRef.current = new Audio(beepSound);
    wrongAudioRef.current = new Audio(wrongSound);
    selectAudioRef.current = new Audio(selectSound);
    removeAudioRef.current = new Audio(removeSound);
  }, []);

  const playBeep = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((error) => {
        console.warn('Audio playback failed:', error);
      });
    }
  }, []);

  const playWrongSound = useCallback(() => {
    if (wrongAudioRef.current) {
      wrongAudioRef.current.currentTime = 0;
      wrongAudioRef.current.play().catch((error) => {
        console.warn('Audio playback failed:', error);
      });
    }
  }, []);

  const playSelectSound = useCallback(() => {
    if (selectAudioRef.current) {
      selectAudioRef.current.currentTime = 0;
      selectAudioRef.current.play().catch((error) => {
        console.warn('Audio playback failed:', error);
      });
    }
  }, []);

  const playRemoveSound = useCallback(() => {
    if (removeAudioRef.current) {
      removeAudioRef.current.currentTime = 0;
      removeAudioRef.current.play().catch((error) => {
        console.warn('Audio playback failed:', error);
      });
    }
  }, []);

  // Filter for valid words from the full word list
  const validWords = Object.keys(wordList as WordList);

  const generateRandomLetters = useCallback(() => {
    const newLetters: string[] = [];
    const letterCounts: { [key: string]: number } = {};

    // Helper function to get a random letter based on frequency
    const getRandomLetter = (pool: string[]): string => {
      const totalWeight = pool.reduce(
        (sum, letter) => sum + NORMALIZED_FREQUENCIES[letter],
        0,
      );
      let random = Math.random() * totalWeight;

      for (const letter of pool) {
        random -= NORMALIZED_FREQUENCIES[letter];
        if (random <= 0) return letter;
      }
      return pool[pool.length - 1];
    };

    // Ensure at least two vowels
    while (newLetters.filter((letter) => VOWELS.includes(letter)).length < 2) {
      const vowel = getRandomLetter(VOWELS);
      if ((letterCounts[vowel] || 0) < 3) {
        newLetters.push(vowel);
        letterCounts[vowel] = (letterCounts[vowel] || 0) + 1;
      }
    }

    // Fill remaining letters
    while (newLetters.length < 10) {
      const isVowel = Math.random() < 0.3; // 30% chance of vowel
      const letterPool = isVowel ? VOWELS : CONSONANTS;
      const letter = getRandomLetter(letterPool);

      // Skip if we already have 3 of this letter
      if ((letterCounts[letter] || 0) >= 3) continue;

      // Special handling for Q
      if (letter === 'Q') {
        // Only add Q if we don't already have it and we have space for U
        if (!newLetters.includes('Q') && newLetters.length < 9) {
          newLetters.push('Q');
          letterCounts['Q'] = 1;
          // Always add U after Q
          newLetters.push('U');
          letterCounts['U'] = (letterCounts['U'] || 0) + 1;
        }
        continue;
      }

      // Skip U if we don't have Q
      if (letter === 'U' && !newLetters.includes('Q')) continue;

      newLetters.push(letter);
      letterCounts[letter] = (letterCounts[letter] || 0) + 1;
    }

    // Shuffle the letters
    return newLetters.sort(() => Math.random() - 0.5);
  }, []);

  const resetRound = useCallback(() => {
    const newLetters = generateRandomLetters();
    setLetters(newLetters);
    setCurrentWord([]);
    setTimeLeft(GAME_TIME);
    setUsedLetters(new Set());
    setErrorMessage(null);
    setAttemptsThisRound(0);

    // Initialize letter usage tracking
    const initialUsage: { [key: string]: number } = {};
    newLetters.forEach((letter) => {
      initialUsage[letter] = 0;
    });
    setLetterUsage(initialUsage);
  }, [generateRandomLetters]);

  const resetGame = useCallback(() => {
    setScore(0);
    setUsedWords([]);
    setGameOver(false);
    resetRound();
  }, [resetRound]);

  // const calculateScore = (wordCount: number) => {
  //   if (wordCount === 1) return 20;
  //   return Math.max(0, 20 - (wordCount - 1) * 2);
  // };

  const addFloatingScore = useCallback(
    (points: number, x: number, y: number) => {
      const id = nextScoreId.current++;
      setFloatingScores((prev) => [...prev, { id, points, x, y }]);
      // Remove the score after animation
      setTimeout(() => {
        setFloatingScores((prev) => prev.filter((score) => score.id !== id));
      }, 1000);
    },
    [],
  );

  const clearCurrentWord = useCallback(() => {
    setCurrentWord([]);
    setLetterUsage((prev) => {
      const newUsage = { ...prev };
      currentWordRef.current.forEach((letter) => {
        newUsage[letter] = 0;
      });
      return newUsage;
    });
  }, []);

  const handleSubmit = useCallback(() => {
    const word = currentWordRef.current.join('');

    // Increment attempts counter
    setAttemptsThisRound((prev) => prev + 1);

    // Check minimum word length
    if (word.length < 3) {
      setIsInvalidWord(true);
      playWrongSound();
      setTimeout(() => {
        setIsInvalidWord(false);
        clearCurrentWord();
      }, 500);
      return;
    }

    if (!validWords.includes(word.toLowerCase())) {
      setIsInvalidWord(true);
      playWrongSound();
      setTimeout(() => {
        setIsInvalidWord(false);
        clearCurrentWord();
      }, 500);
      return;
    }

    // Check if word was already used
    if (usedWords.includes(word.toLowerCase())) {
      setIsInvalidWord(true);
      playWrongSound();
      setTimeout(() => {
        setIsInvalidWord(false);
        clearCurrentWord();
      }, 500);
      return;
    }

    // Add word to used words
    setUsedWords((prev) => [...prev, word.toLowerCase()]);

    // Update used letters for visual feedback
    const newUsedLetters = new Set(usedLetters);
    currentWordRef.current.forEach((letter) => newUsedLetters.add(letter));
    setUsedLetters(newUsedLetters);

    // Handle three-letter word penalty
    if (word.length === 3) {
      setScore((prev) => prev - 10);
      setTimeLeft((prev) => Math.max(0, prev - 20));
      // Add floating score for penalty
      const scoreElement = document.querySelector('.wbg-score');
      if (scoreElement) {
        const rect = scoreElement.getBoundingClientRect();
        addFloatingScore(-5, rect.left, rect.top);
      }
      // Add floating score for time penalty
      const timerElement = document.querySelector('.wbg-timer');
      if (timerElement) {
        const rect = timerElement.getBoundingClientRect();
        addFloatingScore(-10, rect.left, rect.top);
      }
    } else {
      // Award length bonus points immediately for words 5+ letters
      const wordLength = currentWordRef.current.length;
      const lengthBonus = wordLength > 4 ? Math.pow(2, wordLength - 4) : 0;

      if (lengthBonus > 0) {
        setScore((prev) => prev + lengthBonus);
        // Add floating score for length bonus
        const scoreElement = document.querySelector('.wbg-score');
        if (scoreElement) {
          const rect = scoreElement.getBoundingClientRect();
          addFloatingScore(lengthBonus, rect.left, rect.top);
        }
        playBeep();
      }
    }

    // Check if all letters are used
    const allLettersUsed = lettersRef.current.every((letter) =>
      newUsedLetters.has(letter),
    );

    if (allLettersUsed) {
      // Calculate score based on attempts this round
      const roundScore = Math.max(0, 20 - attemptsRef.current);
      setScore((prev) => prev + roundScore);
      // Add floating score for round completion
      const scoreElement = document.querySelector('.wbg-score');
      if (scoreElement) {
        const rect = scoreElement.getBoundingClientRect();
        addFloatingScore(roundScore, rect.left, rect.top);
      }
      playBeep();
      resetRound();
    } else {
      // For valid submissions, just clear the current word and reset letter usage
      clearCurrentWord();
    }
    setErrorMessage(null);
  }, [
    usedWords,
    usedLetters,
    validWords,
    resetRound,
    addFloatingScore,
    playBeep,
    playWrongSound,
    clearCurrentWord,
  ]);

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (gameOverRef.current) return;

      // Handle Enter key for word submission
      if (e.key === 'Enter' && currentWordRef.current.length > 0) {
        handleSubmit();
        return;
      }

      // Handle backspace
      if (e.key === 'Backspace' && currentWordRef.current.length > 0) {
        const lastLetter =
          currentWordRef.current[currentWordRef.current.length - 1];
        setLetterUsage((prev) => ({
          ...prev,
          [lastLetter]: prev[lastLetter] - 1,
        }));
        setCurrentWord((prev) => prev.slice(0, -1));
        setErrorMessage(null);
        playRemoveSound();
        return;
      }

      const key = e.key.toUpperCase();
      if (!/^[A-Z]$/.test(key)) return;

      // Count how many times this letter appears in the available letters
      const availableCount = lettersRef.current.filter((l) => l === key).length;
      // Count how many times this letter is currently used in the word
      const currentUsage = letterUsageRef.current[key] || 0;

      if (currentUsage >= availableCount) return;

      setCurrentWord((prev) => [...prev, key]);
      setLetterUsage((prev) => ({
        ...prev,
        [key]: (prev[key] || 0) + 1,
      }));
      setErrorMessage(null);
      playSelectSound();
    },
    [handleSubmit, playRemoveSound, playSelectSound],
  );

  const handleLetterClick = useCallback(
    (letter: string) => {
      if (gameOverRef.current) return;

      // If we're showing an invalid word, clear it first
      if (isInvalidWord) {
        clearCurrentWord();
        setIsInvalidWord(false);
      }

      // Count how many times this letter appears in the available letters
      const availableCount = lettersRef.current.filter(
        (l) => l === letter,
      ).length;
      // Count how many times this letter is currently used in the word
      const currentUsage = letterUsageRef.current[letter] || 0;

      if (currentUsage >= availableCount) return;

      // Add the letter to the current word
      setCurrentWord((prev) => [...prev, letter]);
      setLetterUsage((prev) => ({
        ...prev,
        [letter]: (prev[letter] || 0) + 1,
      }));
      setErrorMessage(null);
      playSelectSound();
    },
    [isInvalidWord, clearCurrentWord, playSelectSound],
  );

  const handleCurrentWordLetterClick = useCallback(
    (index: number) => {
      if (gameOverRef.current) return;

      // If we're showing an invalid word, clear it first
      if (isInvalidWord) {
        clearCurrentWord();
        setIsInvalidWord(false);
        return;
      }

      const letter = currentWordRef.current[index];
      setCurrentWord((prev) => prev.filter((_, i) => i !== index));
      setLetterUsage((prev) => ({
        ...prev,
        [letter]: prev[letter] - 1,
      }));
      setErrorMessage(null);
      playRemoveSound();
    },
    [isInvalidWord, clearCurrentWord, playRemoveSound],
  );

  const handleSortLetters = useCallback(() => {
    setLetters((prev) => [...prev].sort());
  }, []);

  const handleShuffleLetters = useCallback(() => {
    setLetters((prev) => [...prev].sort(() => Math.random() - 0.5));
  }, []);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKey);
      // Only reset if the game hasn't started yet
      if (letters.length === 0) {
        resetRound();
      }
    }
    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, handleKey, resetRound, letters.length]);

  useEffect(() => {
    if (!isOpen || gameOver) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setGameOver(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, gameOver]);

  useEffect(() => {
    if (gameOver && onHighScore && gameScore && score > (gameScore.user_high_score?.score ?? 0)) {
      onHighScore(score);
    }
  }, [gameOver, score, onHighScore, gameScore]);

  if (!isOpen) return null;

  return (
    <div className="game-overlay">
      <div className="game-modal word-builder">
        <button className="game-close" onClick={onClose || (() => {})}>
          ×
        </button>
        <h2>Tile Pile</h2>
        <div className="wbg-stats row spaced">
          <div className="wbg-score">Score: {score}</div>
          <div className="wbg-letter-controls">
            <IconButton
              title="Sort letters alphabetically"
              icon={faSortAlphaDown}
              size="2x"
              color="var(--dark-green)"
              onClick={handleSortLetters}
            />
            <IconButton
              icon={faRandom}
              size="2x"
              color="var(--dark-green)"
              onClick={handleShuffleLetters}
              title="Shuffle letters randomly"
            />
          </div>
          <div className="wbg-timer">Time: {timeLeft}s</div>
        </div>

        {floatingScores.map(({ id, points, x, y }) => (
          <div
            key={id}
            className={`floating-score ${points < 0 ? 'negative' : ''}`}
            style={{
              left: `${x}px`,
              top: `${y}px`,
            }}
          >
            {points > 0 ? '+' : ''}
            {points}
          </div>
        ))}

        <div className="wbg-letters">
          {letters.map((letter, i) => {
            const letterIndex = letters
              .slice(0, i)
              .filter((l) => l === letter).length;
            const isUsed = letterUsage[letter] > letterIndex;
            return (
              <span
                key={i}
                className={`wbg-letter ${
                  usedLetters.has(letter) ? 'used' : ''
                } ${isUsed ? 'in-use' : ''}`}
                onClick={() => handleLetterClick(letter)}
                role="button"
                tabIndex={0}
              >
                {letter}
              </span>
            );
          })}
        </div>
        {gameOver ? (
          <GameOver
            score={score}
            gameScore={gameScore}
            onPlayAgain={resetGame}
            isHighScore={false}
          />
        ) : (
          <>
            <div
              className={`wbg-current-word letter-count-${currentWord.length} ${
                isInvalidWord ? 'invalid' : ''
              }`}
            >
              {currentWord.map((letter, i) => (
                <span
                  key={i}
                  className="wbg-letter-slot"
                  onClick={() => handleCurrentWordLetterClick(i)}
                  role="button"
                  tabIndex={0}
                >
                  {letter}
                </span>
              ))}
              {currentWord.length < 10 && (
                <span className="wbg-letter-slot active">_</span>
              )}
            </div>
            {errorMessage && <div className="wbg-error">{errorMessage}</div>}
            <ActionButton
              type="go"
              icon={faCheckToSlot}
              label="Submit Word"
              onClick={handleSubmit}
              disabled={currentWord.length === 0}
            />
            <span className="wbg-instructions label-normal">
              Make words using the letters above. Use each letter at least once.
              Three letter words are allowed, but there is a penalty.
            </span>
          </>
        )}
        <CollapsibleWordList words={usedWords} />
      </div>
    </div>
  );
};

export default TilePileGameModal;
